import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const freeOrder = params.get("order");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();
  const { refreshUser } = useAuth();

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      if (freeOrder) { setStatus("paid"); clearCart(); await refreshUser(); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid"); clearCart(); await refreshUser(); return;
        }
      } catch { /* retry */ }
      if (++attempts < 8) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, freeOrder]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="payment-success-page">
      {status === "checking" ? (
        <p className="font-serif text-3xl text-muted-foreground">Confirming your payment…</p>
      ) : (
        <>
          <CheckCircle2 size={44} strokeWidth={1} className="mx-auto text-gold" />
          <h1 className="font-serif text-5xl tracking-tight mt-8">
            {status === "paid" ? "Order confirmed." : "Payment processing."}
          </h1>
          <p className="mt-4 text-ink/60 text-sm leading-relaxed max-w-md mx-auto">
            {status === "paid"
              ? "Thank you for shopping with Jigel. Your order is confirmed and any Cloth Cash applied has been redeemed."
              : "Your payment is still being confirmed. Check My Orders in a few minutes."}
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/account?tab=orders" data-testid="view-orders-btn" className="bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase">My Orders</Link>
            <Link to="/shop" data-testid="success-shop-btn" className="border border-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase">Shop Again</Link>
          </div>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="payment-cancel-page">
      <XCircle size={44} strokeWidth={1} className="mx-auto text-wine" />
      <h1 className="font-serif text-5xl tracking-tight mt-8">Payment cancelled.</h1>
      <p className="mt-4 text-ink/60 text-sm">Your cart is untouched — nothing was charged.</p>
      <div className="mt-10 flex justify-center gap-4">
        <Link to="/checkout" data-testid="retry-checkout-btn" className="bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase">Return to Checkout</Link>
        <Link to="/shop" className="border border-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase">Continue Shopping</Link>
      </div>
    </div>
  );
}
